import { Alert, AlertDialog, Badge, Button, Card, Link, toast } from '@1k5-tech-es/grado-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PhoneCall, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router';

import { api, ApiError } from '../../api/client';
import type { Period, SyncRun, SyncStatus } from '../../api/types';
import { periodLabel } from '../../lib/format';
import { describeSyncError, describeWhen, isClosedMonth, listTimes, radarCovers } from './radar';

/**
 * Calls and talk time come from Radar: when they were last brought, the
 * daily schedule, an "update now" button and who could not be matched.
 */
export function RadarPanel({ period }: { period: Period }) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const status = useQuery({
    queryKey: ['sync'],
    queryFn: () => api.get<SyncStatus>('/api/sync').then((r) => r.radar),
    refetchInterval: (query) => (query.state.data?.running ? 3_000 : 60_000),
  });

  const sync = useMutation({
    mutationFn: () => api.post<{ run: SyncRun }>('/api/sync/radar', period).then((r) => r.run),
    onSuccess: (run) => {
      toast.success(
        run.changedFields ? `Radar: ${run.changedFields} valores actualizados` : 'Radar: ya estaba todo al día',
      );
    },
    onError: (error) => {
      const run = error instanceof ApiError ? (error.details as { run?: SyncRun } | undefined)?.run : undefined;
      toast.error('No se ha actualizado desde Radar', {
        description: run ? describeSyncError(run.errorCode) : error instanceof ApiError && error.status === 409
          ? 'Ya hay una actualización en marcha.'
          : 'Inténtalo de nuevo en unos minutos.',
      });
    },
    onSettled: () => {
      setConfirming(false);
      for (const key of ['sync', 'monthly', 'periods', 'quarters', 'quarter-summary', 'history']) {
        void queryClient.invalidateQueries({ queryKey: [key] });
      }
    },
  });

  const radar = status.data;
  if (!radar) return null;
  if (!radar.enabled) {
    return (
      <p className="mb-4 text-xs text-muted-foreground">
        La importación de llamadas desde Radar no está configurada en este entorno (falta RADAR_API_KEY).
      </p>
    );
  }

  const today = new Date();
  const covered = radarCovers(period, radar.firstMonth, today);
  const closed = isClosedMonth(period, today);
  const busy = sync.isPending || radar.running;
  const last = radar.last;
  const failed = last?.status === 'error' ? last : null;
  const unmatched = radar.lastSuccess?.unmatched ?? [];
  const pending = unmatched.length + radar.withoutEmail.length;

  return (
    <Card className="mb-4 p-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <div className="flex min-w-0 flex-[1_1_18rem] items-center gap-3">
          <PhoneCall aria-hidden className="size-5 shrink-0 text-primary-text" />
          <div className="min-w-0 text-sm">
            <p className="font-semibold">Llamadas y horas de llamada desde Radar</p>
            <p className="text-muted-foreground">
              {radar.lastSuccess
                ? `Actualizado ${describeWhen(radar.lastSuccess.finishedAt ?? radar.lastSuccess.startedAt, today)}`
                : 'Aún no se ha actualizado nunca'}
              {' · '}automático cada día a las {listTimes(radar.times)}
            </p>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {pending > 0 && (
            <Button variant="ghost" size="sm" aria-expanded={showDetail} onClick={() => setShowDetail((v) => !v)}>
              <Badge tone="warning">{pending}</Badge> sin cruzar
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            icon={RefreshCw}
            loading={busy}
            disabled={!covered}
            title={covered ? undefined : 'Radar solo tiene datos desde enero de 2026 hasta el mes en curso'}
            onClick={() => (closed ? setConfirming(true) : sync.mutate())}
          >
            {busy ? 'Actualizando…' : 'Actualizar ahora'}
          </Button>
        </div>
      </div>

      {failed && (
        <Alert tone="danger" title={`La última actualización falló (${describeWhen(failed.startedAt, today)})`} className="mt-3">
          {describeSyncError(failed.errorCode)}
        </Alert>
      )}

      {showDetail && pending > 0 && (
        <div className="mt-3 space-y-2 border-t border-border pt-3 text-sm">
          {unmatched.length > 0 && (
            <p>
              <span className="font-medium">En Radar, pero sin asesor con ese email aquí:</span>{' '}
              <span className="text-muted-foreground">{unmatched.map((u) => `${u.name} (${u.email})`).join(', ')}</span>
            </p>
          )}
          {radar.withoutEmail.length > 0 && (
            <p>
              <span className="font-medium">Asesores activos sin email, que no se pueden cruzar:</span>{' '}
              <span className="text-muted-foreground">{radar.withoutEmail.join(', ')}</span>
            </p>
          )}
          <Link as={RouterLink} to="/asesores" className="text-sm font-semibold">
            Revisar los emails en Asesores →
          </Link>
        </div>
      )}

      <AlertDialog
        open={confirming}
        onOpenChange={(open) => !open && !sync.isPending && setConfirming(false)}
        title={`Traer ${periodLabel(period)} desde Radar`}
        description={`Las llamadas y horas de llamada de ${periodLabel(period)} se sustituirán por las de Radar, también las escritas a mano. Si ya se enviaron informes de ese mes, no cambian.`}
        footer={
          <>
            <Button variant="secondary" disabled={sync.isPending} onClick={() => setConfirming(false)}>
              Cancelar
            </Button>
            <Button icon={RefreshCw} loading={sync.isPending} onClick={() => sync.mutate()}>
              Traer desde Radar
            </Button>
          </>
        }
      />
    </Card>
  );
}
